"use client";

import { useEffect, type RefObject } from "react";

import { useStudio } from "@/contexts/studio-context";

/**
 * Faire défiler le studio depuis une surface qui n'est pas le canvas.
 *
 * ## Le geste déplace le studio, pas la surface
 *
 * La surface qui s'en sert n'a pas de position à elle : elle affiche celle du
 * studio. Un geste dessus n'a donc rien à décaler localement — il conduit la
 * navigation du studio, et la surface bouge parce que le studio a bougé. Le
 * roll bouge en même temps, sans que personne ne les synchronise : c'est le
 * même objet qu'ils déplacent tous les deux.
 *
 * ## Rien n'est réimplémenté
 *
 * Ni l'inertie, ni la mise en pause pendant le geste, ni le `seekToTick` du
 * relâchement, ni l'anti-rebond de la molette : tout est dans `ViewNavigator`,
 * et ce hook ne fait que traduire des pixels en ticks et appeler les mêmes
 * quatre verbes que le roll appelle. Un second gestionnaire aurait son propre
 * easing, et les deux surfaces se seraient écartées au premier réglage.
 *
 * ## Le doigt et la molette ne vont pas dans le même sens, et c'est voulu
 *
 * Le roll fait déjà cette distinction : on **empoigne** le ruban, donc le
 * contenu suit le doigt ; on **pousse** la molette, donc c'est la vue qui
 * avance. Reprendre les deux sens tels quels est la seule façon que les deux
 * surfaces se comportent pareil sous la même main.
 */
export function useStudioNavigation({
  surfaceRef,
  ticksPerPixel,
}: {
  /** La surface qui reçoit le geste. */
  readonly surfaceRef: RefObject<HTMLElement | null>;
  /**
   * Ce qu'un pixel de cette surface vaut en ticks.
   *
   * Sa densité à elle, et non celle du roll : c'est ce qui fait que le dessin
   * suit le doigt exactement, ce que les deux surfaces doivent faire même
   * quand elles ne gravent pas le temps à la même échelle.
   */
  readonly ticksPerPixel: number;
}): void {
  const { studio } = useStudio();

  useEffect(() => {
    const surface = surfaceRef.current;

    if (!studio || surface === null || ticksPerPixel <= 0) {
      return;
    }

    let pointer: number | null = null;
    let lastX = 0;

    const onWheel = (event: WheelEvent) => {
      // Le temps est en abscisse ici, donc les deux axes de la molette y
      // mènent : le pavé tactile glisse en horizontal, la souris roule en
      // vertical, et l'un comme l'autre doit déplacer le morceau.
      const horizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY);
      const pixels = horizontal ? event.deltaX : -event.deltaY;

      // Non passif : sans ça la page défilerait derrière le studio sur les
      // écrans où elle le peut.
      event.preventDefault();
      studio.wheelViewOffset(pixels * ticksPerPixel);
    };

    const onPointerDown = (event: PointerEvent) => {
      if (pointer !== null || !event.isPrimary) {
        return;
      }

      pointer = event.pointerId;
      lastX = event.clientX;
      studio.beginViewGesture();

      /*
        La capture ensuite, et sans qu'elle puisse tout emporter. Elle sert à
        ce que le geste survive au doigt qui sort de la partition, ce qui
        arrive tout le temps puisqu'elle ne fait que deux cents pixels de
        haut. Mais elle lève sur un pointeur que le navigateur ne tient pas
        pour actif, et un geste perdu là laisserait le transport en pause et
        le décalage de vue ouvert, sans personne pour les refermer. Le geste
        passe donc d'abord, la capture est un confort.
      */
      try {
        surface.setPointerCapture(event.pointerId);
      } catch {
        // Sans capture : le geste vit tant que le doigt reste sur la surface.
      }
    };

    const onPointerMove = (event: PointerEvent) => {
      if (pointer !== event.pointerId) {
        return;
      }

      const deltaX = event.clientX - lastX;

      lastX = event.clientX;
      // Vers la gauche, on tire l'avenir à soi. C'est le geste du roll, où
      // l'on tire vers le bas ce qui vient : le contenu suit le doigt.
      studio.updateViewOffset(-deltaX * ticksPerPixel);
    };

    const onPointerUp = (event: PointerEvent) => {
      if (pointer !== event.pointerId) {
        return;
      }

      pointer = null;

      if (surface.hasPointerCapture(event.pointerId)) {
        surface.releasePointerCapture(event.pointerId);
      }

      studio.endViewGesture();
    };

    surface.addEventListener("wheel", onWheel, { passive: false });
    surface.addEventListener("pointerdown", onPointerDown);
    surface.addEventListener("pointermove", onPointerMove);
    surface.addEventListener("pointerup", onPointerUp);
    surface.addEventListener("pointercancel", onPointerUp);

    return () => {
      surface.removeEventListener("wheel", onWheel);
      surface.removeEventListener("pointerdown", onPointerDown);
      surface.removeEventListener("pointermove", onPointerMove);
      surface.removeEventListener("pointerup", onPointerUp);
      surface.removeEventListener("pointercancel", onPointerUp);

      // Un geste laissé en plan au démontage tiendrait le transport en pause
      // et le décalage de vue ouvert. On le referme immédiatement, sans
      // inertie : personne n'est plus là pour la regarder.
      if (pointer !== null) {
        studio.endViewGesture(true);
      }
    };
  }, [studio, surfaceRef, ticksPerPixel]);
}
